import type { ThemeMode } from '@/hooks/useLightMode'
import getConfig from 'next/config'

import { Dict } from 'mixpanel-browser'

import type { Role } from '@/types/roles'
import { mixpanel } from '@/utils/analytics/mixpanel'

const { publicRuntimeConfig } = getConfig()

/**
 * - Call this method to start tracking the duration of an event.
 * - Call `trackEvent` with the same name to complete the timing, and track
 * that the event has completed.
 *
 * e.x. If you have code like the following:
 * ```js
 * startTimer("My button that kicks off a network request")
 * // Request that takes 634ms to run
 * trackEvent(
 *   // name must match the above!
 *   "My button that kicks off a network request",
 *   { "some": "metadata" }
 * )
 * ```
 *
 * The resulting entry in Mixpanel will auto-magically have a `Duration` value of 0.634
 * @param name The thing being tracked
 */
export const startTimer = (name: string) => {
  // eslint-disable-next-line no-console
  // console.log(`useAnalytics: Starting a timer for event [${name}]`)
  mixpanel.time_event(name)
}

/**
 * Used to track that an event has taken place.
 *
 *
 * If called after a call to `startTimer` with a `name` of the same value, a `Duration`
 * property will be automatically bundled into the metadata.
 *
 * e.x. If you have code like the following:
 * ```js
 * startTimer("My button that kicks off a network request")
 * // Request that takes 634ms to run
 * trackEvent(
 *   // name must match the above!
 *   "My button that kicks off a network request",
 *   { "some": "metadata" }
 * )
 * ```
 *
 * The resulting entry in Mixpanel will auto-magically have a `Duration` value of 0.634.
 *
 * @param name The thing being tracked
 * @param metadata Other metadata that you wish to have bundled into the event
 */
export const trackEvent = (name: string, metadata?: Dict) => {
  // eslint-disable-next-line no-console
  // console.log(
  //   `useAnalytics: Recording event: [${name}] with metadata:`,
  //   metadata
  // )
  mixpanel.track(name, metadata)
}

/**
 * - Call this method to register mixpanel super properties which will be recorded in all mixpanel events
 * - https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#setting-super-properties
 *
 * @param url The page url to update and track
 */
export const registerProperties = (url?: string) => {
  // Remove parameters and suffix '/'
  const page = url?.split('?')?.[0]?.replace(/\/$/, '') || '/'
  mixpanel.register({
    env: publicRuntimeConfig?.env,
    page,
    version: publicRuntimeConfig?.version,
  })
}

/**
 * - Registers the active theme (`light` / `dark`) as a mixpanel super property
 * so it lands on every event (ENG-33713)
 * - Called by `ThemeAnalytics`, which is mounted once in `_app.tsx`; nothing
 * else should call this
 *
 * `mixpanel.register` merges rather than replaces, so `registerProperties`
 * re-registering `env` / `page` / `version` on every route change leaves this
 * untouched — it does not need to re-assert the theme.
 *
 * @param mode The theme the app is currently rendering in
 */
export const registerThemeProperty = (mode: ThemeMode) => {
  mixpanel.register({ theme: mode })
}

/**
 * - Registers the signed-in user's roles as mixpanel super properties so they
 * land on every event
 * - `role` is the deprecated single `roleName`, kept so existing reports keep
 * working; `roles` is the full list now that users can hold many roles. Mobile
 * registers the same two keys
 * - Called by `useIdentifyUser`; nothing else should call this
 *
 * @param role The user's deprecated single role
 * @param roles Every role the user holds
 */
export const registerRoleProperties = (
  role: Role | undefined,
  roles: Role[]
) => {
  mixpanel.register({ role, roles })
}

/**
 * Clears the role super properties on logout. They persist in the mixpanel
 * cookie, so without this the next session's anonymous events would carry the
 * previous user's roles.
 */
export const unregisterRoleProperties = () => {
  mixpanel.unregister('role')
  mixpanel.unregister('roles')
}
